import { useState } from 'react';
import { HelpCircle, ChevronDown } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

/* ─── FAQ data ─── */
const FAQ_ITEMS = [
  {
    q: 'What is FrenchBoard?',
    a: "FrenchBoard is Canada's first turnkey French smart-board activity engine for Grade 4-6 classrooms. Open it, pick the grade, month, and topic, and run a full French activity on the board right away.",
  },
  {
    q: "What's included?",
    a: 'Everything for the teaching day, all aligned to your provincial curriculum: French games, lesson slides, activities, and one-click printable recall sheets.',
  },
  {
    q: 'What grade and level is it for?',
    a: 'Grade 4, 5, and 6 Core French.',
  },
  {
    q: 'Is it aligned to our curriculum?',
    a: "Yes. Every session is mapped to your province's curriculum expectations.",
  },
  {
    q: 'Is the French Canadian?',
    a: 'Yes. Canadian French, built from the ground up for Canadian classrooms.',
  },
  {
    q: 'Do students need accounts, and is any data collected?',
    a: "No on both. No student logins, no student data, nothing collected. It runs on the teacher's board through a browser.",
  },
  {
    q: 'What do we need to run it, and does it touch our IT?',
    a: 'Just a browser on your smartboard, projector, or classroom computer. It runs as-is, nothing to install or set up.',
  },
  {
    q: 'How many teachers can use it?',
    a: 'No limit. Every Grade 4, 5, and 6 French teacher runs their own divisions and as many sections as you have, each with its own clean progress.',
  },
  {
    q: 'How much of the year does it cover?',
    a: 'The full year. Games and lesson slides for every teaching day across all 10 months, with flexible routines so you pick the cadence that fits your week. Every slide carries the exact words and phrases for student outcomes, plus recall sheets students take home.',
  },
  {
    q: 'What does it cost?',
    a: "For a limited time, you can lock in the full next school year at just $160/month. It's kept budget-friendly and simple, so a teacher can bring it into class and start using it right away.",
  },
];

function FaqItem({ item, expanded, onToggle, index }) {
  return (
    <div
      data-testid={`faq-item-${index}`}
      style={{
        border: '1px solid #E2E8F0', borderRadius: '0.625rem',
        background: 'white', overflow: 'hidden',
        transition: 'box-shadow 0.2s',
        boxShadow: expanded ? '0 2px 12px rgba(0,0,0,0.06)' : 'none',
      }}
    >
      <button
        onClick={onToggle}
        data-testid={`faq-toggle-${index}`}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          width: '100%', padding: '0.875rem 1rem', background: 'transparent',
          border: 'none', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <ChevronDown
          size={16}
          color="#94A3B8"
          style={{ flexShrink: 0, transition: 'transform 0.2s', transform: expanded ? 'rotate(0)' : 'rotate(-90deg)' }}
        />
        <span style={{
          flex: 1, fontFamily: 'Outfit, sans-serif', fontSize: '0.975rem',
          fontWeight: 600, color: '#1A1D20', lineHeight: 1.4,
        }}>
          {item.q}
        </span>
      </button>
      {expanded && (
        <div style={{ padding: '0 1rem 1rem 2.75rem', borderTop: '1px solid #F1F5F9' }}>
          <p style={{
            fontFamily: 'Nunito, sans-serif', fontSize: '0.925rem', color: '#374151',
            lineHeight: 1.7, margin: '0.875rem 0 0',
          }}>
            {item.a}
          </p>
        </div>
      )}
    </div>
  );
}

export function TeacherFaq() {
  const [expanded, setExpanded] = useState(new Set([0]));
  const { t } = useLanguage();

  const toggle = (idx) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <div
      data-testid="teacher-faq-page"
      style={{ height: '100%', background: '#F8F9FB', overflow: 'auto', padding: '2rem 2.5rem 4rem' }}
    >
      <div style={{ maxWidth: 720 }}>
        <div style={{ marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          <HelpCircle size={22} color="#1E2140" />
          <h1
            data-testid="faq-title"
            style={{
              fontFamily: 'Outfit, sans-serif', fontSize: '1.75rem', fontWeight: 700,
              color: '#1A1D20', margin: 0,
            }}
          >
            {t('faq.title') || 'FAQ'}
          </h1>
        </div>
        <p style={{
          fontFamily: 'Nunito, sans-serif', fontSize: '1rem',
          color: '#64748B', margin: '0 0 1.75rem', lineHeight: 1.6,
        }}>
          {t('faq.subtitle') || 'Quick answers to the questions teachers and admins ask most often.'}
        </p>

        <div
          data-testid="faq-list"
          style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
        >
          {FAQ_ITEMS.map((item, idx) => (
            <FaqItem
              key={idx}
              index={idx}
              item={item}
              expanded={expanded.has(idx)}
              onToggle={() => toggle(idx)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
